// Routes for the tournament web app.
//
// Four audiences, and which of them needs an account is the whole security story:
//
//   /console…    — the host. A REAL account, behind AuthGate.
//   /join/:code  — a captain registering a team. No account; the join code is the ticket.
//   /score/:code — a group's scorekeeper on the course. Anonymous auth, no signup, because
//                  nobody creates an account standing on a tee box with a horn about to go.
//   /e/:slug     — the clubhouse screen and anyone following along. No auth at all.
//
// The join code and the public slug are DIFFERENT secrets on purpose: sharing a leaderboard
// must never hand somebody the ability to enter the field or post a score.
//
// Client-side routing with a Netlify SPA fallback (public/_redirects + netlify.toml), which
// is what lets /e/<slug> be a real URL on a static host with nothing rendering it.

import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { CONFIGURED } from './lib/supabase';
import AuthGate from './components/AuthGate';
import SignupPage from './pages/SignupPage';
import ScorePage from './pages/ScorePage';
import LeaderboardPage from './pages/LeaderboardPage';
import DeleteAccountPage from './pages/DeleteAccountPage';

// The console is by far the heaviest part of the app and the only part a golfer never
// loads. Splitting it out keeps the two pages that get opened ON A COURSE — scoring and the
// leaderboard — off the critical path, which matters because those are fetched over the
// worst connection any of this will ever see. Staff load the console from a clubhouse desk.
const ConsolePage = lazy(() => import('./pages/ConsolePage'));
const EventPage = lazy(() => import('./pages/EventPage'));
// The Games Market moderation queue — staff only, same as the console, and just as much
// off a golfer's critical path.
const MarketModerationPage = lazy(() => import('./pages/MarketModerationPage'));

function NotConfigured() {
  return (
    <div className="wrap">
      <h1>4sum Tournaments</h1>
      <div className="card">
        <p>
          This build has no Supabase credentials, so there is nothing to show. Set{' '}
          <code>VITE_SUPABASE_URL</code> and <code>VITE_SUPABASE_PUBLISHABLE_KEY</code> and
          rebuild.
        </p>
      </div>
    </div>
  );
}

/** Keeps the split invisible: a spinner would flash for a chunk that loads in milliseconds. */
function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<div className="wrap" />}>{children}</Suspense>;
}

export default function App() {
  if (!CONFIGURED) return <NotConfigured />;
  return (
    <Routes>
      {/* Host. AuthGate wraps the event page too — without it that route would load for
          anyone with the id. RLS would still refuse the data, but showing a signed-out
          person a broken console is a worse answer than asking them to sign in. */}
      <Route path="/" element={<Lazy><ConsolePage /></Lazy>} />
      <Route
        path="/console/event/:id"
        element={
          <AuthGate>
            <Lazy>
              <EventPage />
            </Lazy>
          </AuthGate>
        }
      />
      <Route path="/console/*" element={<Lazy><ConsolePage /></Lazy>} />

      {/* Games Market review queue. AuthGate-wrapped like the console; the RPC itself is
          the actual moderator check (is_preset_moderator()), so a signed-in non-moderator
          just sees an empty queue rather than anything they shouldn't. */}
      <Route
        path="/market/moderation"
        element={
          <Lazy>
            <MarketModerationPage />
          </Lazy>
        }
      />

      {/* Captain registering a team. */}
      <Route path="/join/:code" element={<SignupPage />} />
      <Route path="/join" element={<SignupPage />} />

      {/* Scorekeeper on the course. */}
      <Route path="/score/:code" element={<ScorePage />} />
      <Route path="/score" element={<ScorePage />} />

      {/* Public leaderboard. */}
      <Route path="/e/:slug" element={<LeaderboardPage />} />

      {/* Public account-deletion request page (Google Play requirement). Email one-time
          code only; never creates an anonymous session. */}
      <Route path="/delete-account" element={<DeleteAccountPage />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
